import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { dataLayer } from '@weatherbug/common';

import { reduxConnect } from '../../../../common';

import { TaboolaFeedWidget } from '../../../ads/taboolaFeedWidget.jsx';
import type { IStoreState, Video } from '../../interfaces/types';
import { CommonWidgetHeader } from '../../widgets/commonWidgetHeader';
import { AnimatedLoadingPlaceholder } from '../../animatedLoadingPlaceholder/animatedLoadingPlaceholder';
import { FeaturedVideosWidgetView } from '../../../featuredVideos/featuredVideosWidgetView';
import { WidgetErrorUI } from '../../../widgetErrorUI/WidgetErrorUI';

const { getVideos } = dataLayer.actions;

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { appState, video } = storeState || {};
    const { authId, secret, videoBaseUrl, enableVideoLandingPageOnWeb2, enableTaboola } = appState?.appState || {};
    const { lastError } = video ?? {};

    return { 
        isEnabled: enableTaboola,
        authId,
        secret,
        videoBaseUrl,
        featuredVideos: video?.videos,
        lastError,
        isFetchingVideos: video?.videosIsFetching,
        enableVideoLandingPageOnWeb2
    };
};

const mapDispatchToProps = {
    getVideos
};

type TaboolaVideoReelProps = {
    isEnabled: boolean;
    videoBaseUrl: string;
    authId: string;
    secret: string;
    featuredVideos: Video[];
    getVideos: typeof getVideos;
    isFetchingVideos: boolean;
    lastError: Record<string, unknown>;
    enableVideoLandingPageOnWeb2: boolean;
}

const widgetTitle = { widgetTitle: 'Featured Videos' };
export const TaboolaVideoReel = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ 
    isEnabled = false,
    getVideos, 
    videoBaseUrl,
    featuredVideos, 
    authId,
    secret,
    isFetchingVideos,
    lastError,
    enableVideoLandingPageOnWeb2
}: TaboolaVideoReelProps) => {
    const isInitialLoadComplete = useRef(false);
    const [enableNewVideoLanding, setEnableNewVideoLanding] = useState<boolean>(true);

    useEffect(() => {
        try {
            if (authId && secret && videoBaseUrl && !isInitialLoadComplete.current) {
                const page = 1;
                const pageSize = 20;
                const verbose = false; 
                getVideos(videoBaseUrl, authId, secret, page, pageSize, verbose);
                isInitialLoadComplete.current = true;
            }
        } catch (error) {
            console.error('Error fetching featured videos widget video', error);
        }
    }, [videoBaseUrl, authId, secret, enableVideoLandingPageOnWeb2]);

    useEffect(() => {
        if (enableVideoLandingPageOnWeb2 != undefined) {
            setEnableNewVideoLanding(enableVideoLandingPageOnWeb2);
        }
    }, [enableVideoLandingPageOnWeb2]);

    if (isFetchingVideos && !featuredVideos?.length) {
        return (
            <LoadingContainer>
                <AnimatedLoadingPlaceholder radius={0} height='100%' />
            </LoadingContainer>
        );
    }

    if (!isFetchingVideos && !featuredVideos?.length && lastError) {
        return (
            <ErrorContainer>
                <WidgetErrorUI/>
            </ErrorContainer>
        );
    }

    const widgetLinkURL = featuredVideos?.[0]?.plid || '/weather-videos';

    if (isEnabled) {
        return (
            <WidgetWrapper>
                {enableVideoLandingPageOnWeb2 && <HeaderLink href={widgetLinkURL} target='_self' aria-label='Go to Featured Videos'>
                    <CommonWidgetHeader headerText={widgetTitle} />
                </HeaderLink>}
                {!enableVideoLandingPageOnWeb2 && <HeaderLink href={widgetLinkURL} aria-label='Go to Featured Videos'>
                    <CommonWidgetHeader headerText={widgetTitle} />
                </HeaderLink>}
                <WidgetContainerContent> 
                    <TaboolaContentWrapper>
                        <TaboolaFeedWidget width={'100%'} height={'100%'}  position={'above_content'} />
                    </TaboolaContentWrapper>
                </WidgetContainerContent>
            </WidgetWrapper>
        );
    } else {
        return <FeaturedVideosWidgetView widgetLinkURL={widgetLinkURL} enableVideoLandingPageOnWeb2={enableNewVideoLanding} />;
    }
});

TaboolaVideoReel.displayName = 'TaboolaVideoReel';

const WidgetWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 37px 1fr;
    height: 100%;
    min-height: 250px;
    background-color: #eee;
    overflow: hidden;
`;
const WidgetContainerContent = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const HeaderLink = styled.a`
    display: flex;
    width: 100%;
    height: 100%;
    text-transform: uppercase;
    text-decoration: none;
    background-color: rgb(255, 255, 255);
`;

const TaboolaContentWrapper = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
`;

const DefaultContainer = css`
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    background-color: #f9f9f9;
    border-bottom-right-radius: 2px;
    border-bottom-left-radius: 2px;
    min-height: 250px;
    height: 100%;
`;

const LoadingContainer = styled.div`
    ${DefaultContainer}
    height: 250px;
`;

const ErrorContainer = styled.div`
    ${DefaultContainer}
    justify-content: center;
`;
